import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';
import axios from 'axios';
import api from '../utils/api';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  LabelOutlined as StatusIcon,
} from '@mui/icons-material';

const CustomerDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // 상호작용 다이얼로그 상태
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('add');
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [formData, setFormData] = useState({
    type: 'PHONE',
    date: new Date().toISOString().slice(0, 16),
    notes: '',
    sale_result: false,
  });
  
  // 고객 수정 다이얼로그 상태
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    category_id: '',
    organization: '',
    phone: '',
    email: '',
    region: '',
    position: '',
    status_id: '',
    memo: '',
  });
  
  // 삭제 확인 다이얼로그
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [interactionToDelete, setInteractionToDelete] = useState(null);

  // 고객 정보 및 상호작용 불러오기
  useEffect(() => {
    const fetchCustomerData = async () => {
      setLoading(true);
      try {
        // 고객 정보 불러오기
        const customerRes = await api.get(`/customers/${id}`);
        setCustomer(customerRes.data);
        
        // 고객 수정 폼 데이터 초기화
        setCustomerFormData({
          name: customerRes.data.name || '',
          category_id: customerRes.data.category_id || '',
          organization: customerRes.data.organization || '',
          phone: customerRes.data.phone || '',
          email: customerRes.data.email || '',
          region: customerRes.data.region || '',
          position: customerRes.data.position || '',
          status_id: customerRes.data.status_id || '',
          memo: customerRes.data.memo || '',
        });
        
        // 상호작용 기록 불러오기
        const interactionsRes = await api.get(`/interactions/customer/${id}`);
        console.log('상호작용 데이터:', interactionsRes.data);
        setInteractions(interactionsRes.data);
        
        // 상호작용 통계 불러오기
        const statsRes = await api.get(`/interactions/customer/${id}/stats`);
        console.log('상호작용 통계:', statsRes.data);
        setStats(statsRes.data);

        // 카테고리 불러오기
        const categoriesRes = await api.get('/categories');
        setCategories(categoriesRes.data);

        // 상태 불러오기
        const statusesRes = await api.get('/statuses');
        setStatuses(statusesRes.data);
        
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || '데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  // 상호작용 타입에 따른 색상
  const getTypeColor = (type) => {
    switch (type) {
      case '전화':
        return 'primary';
      case '이메일':
        return 'info';
      case '미팅':
        return 'success';
      case '문자':
        return 'warning';
      default:
        return 'default';
    }
  };

  // 상호작용 타입에 따른 한글 라벨
  const getTypeLabel = (type) => {
    switch (type) {
      case '전화':
        return '전화';
      case '이메일':
        return '이메일';
      case '미팅':
        return '미팅';
      case '문자':
        return '문자';
      default:
        return '기타';
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 폼 데이터 초기화
  const resetFormData = () => {
    // 현재 날짜와 시간을 로컬 시간대로 가져오기
    const now = new Date();
    const localDateStr = now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0') + 'T' +
      String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0');
    
    setFormData({
      type: '전화',
      date: localDateStr,
      notes: '',
      sale_result: false,
    });
  };

  // 다이얼로그 열기
  const handleOpenDialog = (action, interaction = null) => {
    setDialogAction(action);
    
    if (action === 'edit' && interaction) {
      setSelectedInteraction(interaction);
      setFormData({
        type: interaction.type,
        date: new Date(interaction.date).toISOString().slice(0, 16),
        notes: interaction.notes || '',
        sale_result: interaction.sale_result || false,
      });
    } else {
      resetFormData();
    }
    
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetFormData();
  };

  // 고객 수정 다이얼로그 열기
  const handleOpenCustomerDialog = () => {
    setOpenCustomerDialog(true);
  };

  // 고객 수정 다이얼로그 닫기
  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
  };

  // 고객 수정 폼 입력 처리
  const handleCustomerInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerFormData({
      ...customerFormData,
      [name]: value,
    });
  };

  // 고객 정보 업데이트
  const handleCustomerSubmit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/customers/${id}`, customerFormData);
      
      // 고객 정보 다시 불러오기
      const customerRes = await api.get(`/customers/${id}`);
      setCustomer(customerRes.data);
      
      handleCloseCustomerDialog();
      showSnackbar('고객 정보가 성공적으로 업데이트되었습니다', 'success');
    } catch (err) {
      console.error('고객 정보 업데이트 중 오류가 발생했습니다:', err);
      showSnackbar('고객 정보 업데이트 중 오류가 발생했습니다', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // 상호작용 추가 또는 수정 폼 제출
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // 날짜 처리 - 입력된 현지 시간 그대로 사용
      let formattedDate = formData.date;
      if (formData.date) {
        // 가능한 ISO 형식으로 변환
        const localDate = new Date(formData.date);
        formattedDate = localDate.getFullYear() + '-' +
            String(localDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(localDate.getDate()).padStart(2, '0') + 'T' +
            String(localDate.getHours()).padStart(2, '0') + ':' +
            String(localDate.getMinutes()).padStart(2, '0') + ':00';
      }
      
      const interactionData = {
        customer_id: id,
        type: formData.type,
        date: formattedDate,
        notes: formData.notes,
        sale_result: formData.sale_result,
        created_by: user.id,
      };
      
      console.log('전송할 데이터:', interactionData);
      
      if (dialogAction === 'edit') {
        await api.put(`/interactions/${selectedInteraction.id}`, interactionData);
      } else {
        await api.post('/interactions', interactionData);
      }
      
      // 상호작용 기록 새로고침
      const interactionsRes = await api.get(`/interactions/customer/${id}`);
      setInteractions(interactionsRes.data);
      
      // 상호작용 통계 새로고침
      const statsRes = await api.get(`/interactions/customer/${id}/stats`);
      setStats(statsRes.data);
      
      handleCloseDialog();
      resetFormData();
      setError(null);
    } catch (err) {
      console.error('상호작용 저장 오류:', err);
      setError(err.response?.data?.message || '저장하는 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 폼 입력 변경
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (interaction) => {
    setInteractionToDelete(interaction);
    setDeleteConfirm(true);
  };

  // 상호작용 삭제
  const handleDelete = async () => {
    try {
      await api.delete(`/interactions/${interactionToDelete.id}`);
      setDeleteConfirm(false);
      
      // 상호작용 기록 새로고침
      const interactionsRes = await api.get(`/interactions/customer/${id}`);
      setInteractions(interactionsRes.data);
      
      // 상호작용 통계 새로고침
      const statsRes = await api.get(`/interactions/customer/${id}/stats`);
      setStats(statsRes.data);
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '삭제하는 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/customers')}
          startIcon={<ArrowBackIcon />}
        >
          고객 목록으로
        </Button>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="warning">고객 정보를 찾을 수 없습니다.</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/customers')}
          startIcon={<ArrowBackIcon />}
        >
          고객 목록으로
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          component={Link}
          to="/customers"
          startIcon={<ArrowBackIcon />}
        >
          고객 목록으로
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="h2">기본 정보</Typography>
                <IconButton 
                  size="small" 
                  onClick={handleOpenCustomerDialog}
                  title="고객 정보 수정"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* 고객 이름 표시 */}
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                {customer?.name}
              </Typography>
              
              {customer?.category_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.category_name}</Typography>
                </Box>
              )}
              
              {customer?.organization && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.organization}</Typography>
                </Box>
              )}
              
              {customer?.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.phone}</Typography>
                </Box>
              )}
              
              {customer?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.email}</Typography>
                </Box>
              )}
              
              {customer?.region && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.region}</Typography>
                </Box>
              )}
              
              {customer?.position && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{customer.position}</Typography>
                </Box>
              )}
              
              {customer?.status_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StatusIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        backgroundColor: customer.status_color || 'transparent',
                        mr: 1,
                        border: customer.status_color ? 'none' : '1px dashed #ccc'
                      }} 
                    />
                    <Typography variant="body2">{customer.status_name}</Typography>
                  </Box>
                </Box>
              )}
              
              {customer?.memo && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>메모</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {customer.memo}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    성공한 판매
                  </Typography>
                  <Typography variant="h3" color="success.main">
                    {stats?.salesCount || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    미팅 횟수
                  </Typography>
                  <Typography variant="h3" color="info.main">
                    {stats?.meetingCount || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">상호작용 기록</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog('add')}
                  >
                    상호작용 추가
                  </Button>
                </Box>
                
                {interactions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      아직 기록된 상호작용이 없습니다.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog('add')}
                      sx={{ mt: 1 }}
                    >
                      첫 상호작용 추가하기
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {interactions.map((interaction) => (
                      <Paper key={interaction.id} sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              label={getTypeLabel(interaction.type)}
                              color={getTypeColor(interaction.type)}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(interaction.date)}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', interaction)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(interaction)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        {/* 내용이 비어있지 않고, 숫자만 있는 경우가 아닐 때만 표시 */}
                        {interaction.notes && interaction.notes.trim() !== "" && !/^[0-9]+$/.test(interaction.notes.trim()) && (
                          <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                            {interaction.notes}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          {interaction.sale_result ? (
                            <Chip label="판매 성공" color="success" size="small" />
                          ) : (
                            <Box />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            작성자: {interaction.user_name || '알 수 없음'}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* 상호작용 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === 'add' ? '상호작용 추가' : '상호작용 수정'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>상호작용 유형</InputLabel>
            <Select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              label="상호작용 유형"
            >
              <MenuItem value="전화">전화</MenuItem>
              <MenuItem value="미팅">미팅</MenuItem>
              <MenuItem value="이메일">이메일</MenuItem>
              <MenuItem value="문자">문자</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            name="date"
            label="날짜 및 시간"
            type="datetime-local"
            fullWidth
            value={formData.date}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            name="notes"
            label="내용"
            multiline
            rows={4}
            fullWidth
            value={formData.notes}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                name="sale_result"
                checked={formData.sale_result}
                onChange={handleInputChange}
                color="success"
              />
            }
            label="판매 성공"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : (
              dialogAction === 'add' ? '추가' : '저장'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>상호작용 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            이 상호작용 기록을 정말로 삭제하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 고객 정보 수정 다이얼로그 */}
      <Dialog open={openCustomerDialog} onClose={handleCloseCustomerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          고객 정보 수정
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="이름"
            fullWidth
            value={customerFormData.name}
            onChange={handleCustomerInputChange}
            margin="normal"
            required
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>카테고리</InputLabel>
            <Select
              name="category_id"
              value={customerFormData.category_id}
              onChange={handleCustomerInputChange}
              label="카테고리"
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            name="organization"
            label="소속"
            fullWidth
            value={customerFormData.organization}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
          
          <TextField
            name="phone"
            label="전화번호"
            fullWidth
            value={customerFormData.phone}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
          
          <TextField
            name="email"
            label="이메일"
            fullWidth
            value={customerFormData.email}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
          
          <TextField
            name="region"
            label="지역"
            fullWidth
            value={customerFormData.region}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
          
          <TextField
            name="position"
            label="직급"
            fullWidth
            value={customerFormData.position}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>상태</InputLabel>
            <Select
              name="status_id"
              value={customerFormData.status_id}
              onChange={handleCustomerInputChange}
              label="상태"
            >
              {statuses.map((status) => (
                <MenuItem 
                  key={status.id} 
                  value={status.id}
                  sx={{
                    backgroundColor: status.color || 'transparent',
                    '&:hover': {
                      backgroundColor: status.color ? `${status.color}!important` : 'inherit'
                    }
                  }}
                >
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            name="memo"
            label="메모"
            fullWidth
            multiline
            rows={3}
            value={customerFormData.memo}
            onChange={handleCustomerInputChange}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomerDialog}>취소</Button>
          <Button onClick={handleCustomerSubmit} variant="contained" disabled={submitting}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetail; 